import React, { useEffect, useState } from 'react';
import { createPortal } from 'react-dom';
import { X } from 'lucide-react';
import { ResolvedPeriod } from '../../PeriodPicker';
import { useEstimationUnit, type EstimationUnit } from '../../estimation/estimationUnit';
import { BucketDrillDownResponse, DynamicsDiagnostics, QualityApi } from '../qualityApi';
import { JIRA_BROWSE_PREFIX } from '../../../../shared/jiraBrowseUrl';
import {
  bugOriginReporterCellClass,
  bugOriginReporterColor,
  resolveBugOriginSeries,
} from '../../../../shared/bugOriginReporter';
const PAGE_SIZE = 100;

interface Props {
  metric: Parameters<typeof QualityApi.getBucketDrillDown>[1]['metric'];
  groupBy: 'sprint' | 'release' | 'week' | 'month';
  bucketLabel: string;
  /** Same title as the Dynamics card `<h3>` (e.g. "Bug arrival vs closure"). */
  chartTitle?: string;
  points: Array<Record<string, number | string | null>>;
  /** Same diagnostics as the parent Dynamics card (used for bug-origin legend / preview). */
  dynamicsDiagnostics?: DynamicsDiagnostics | null;
  resolvedPeriod: ResolvedPeriod | null;
  boardId: string | null;
  onClose: () => void;
}

/**
 * Modal that lists Jira issues contributing to a single point on a Dynamics
 * chart. Uses `/api/analytics/quality/bucket-drill-down` with the bucket's
 * `groupBy` + `bucketKey` so the server knows whether to scope by sprint id,
 * release, or a date range (for week/month buckets).
 */
export const BucketDrilldownModal: React.FC<Props> = ({
  metric,
  groupBy,
  bucketLabel,
  chartTitle,
  points,
  dynamicsDiagnostics,
  resolvedPeriod,
  boardId,
  onClose,
}) => {
  const { unit, suffix, toUnitValue, parenSuffix } = useEstimationUnit();
  const [data, setData] = useState<BucketDrillDownResponse | null>(null);
  const [offset, setOffset] = useState(0);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    setData(null);
    setOffset(0);
  }, [bucketLabel, metric, groupBy, resolvedPeriod?.startDate, resolvedPeriod?.endDate, boardId]);

  useEffect(() => {
    let cancelled = false;
    const run = async () => {
      setLoading(true);
      setError(null);
      try {
        // Server identifies the bucket by its (groupBy, key) pair. For non-sprint
        // buckets the label is identical to the key; for sprint buckets we may
        // have numeric ids, but backends can accept either sprint_id or sprint_name,
        // so we pass the visible label as the fallback key.
        const res = await QualityApi.getBucketDrillDown(
          { period: null, resolvedPeriod },
          {
            metric,
            groupBy,
            bucketKey: bucketLabel,
            bucketLabel,
            boardId,
            limit: PAGE_SIZE,
            offset,
          }
        );
        if (cancelled) return;
        if (offset === 0) setData(res);
        else setData((prev) => (prev ? { ...res, rows: [...prev.rows, ...res.rows] } : res));
      } catch (err) {
        if (!cancelled) setError(err instanceof Error ? err.message : 'Failed to load');
      } finally {
        if (!cancelled) setLoading(false);
      }
    };
    run();
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    offset,
    bucketLabel,
    metric,
    groupBy,
    resolvedPeriod?.startDate,
    resolvedPeriod?.endDate,
    boardId,
  ]);

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
    };
    document.addEventListener('keydown', onKey);
    const prev = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => {
      document.removeEventListener('keydown', onKey);
      document.body.style.overflow = prev;
    };
  }, [onClose]);

  const rows = data?.rows ?? [];
  const total = data?.total ?? 0;
  const bucket = data?.bucket ?? null;
  const hasMore = rows.length < total;
  const bucketStart = bucket?.start ? bucket.start.slice(0, 10) : null;
  const bucketEnd = bucket?.end ? bucket.end.slice(0, 10) : null;

  // Try to compute a quick metric preview from the parent chart's points so
  // the user sees what this bucket represents before the API responds.
  const thisPoint = points.find((p) => p.period === bucketLabel) ?? null;
  const bugOriginSeriesResolved = resolveBugOriginSeries(dynamicsDiagnostics ?? null);
  const previewSummary =
    metric === 'mttr' && thisPoint
      ? formatMttrBucketPreview(thisPoint, unit, toUnitValue, suffix)
      : thisPoint
        ? buildPreviewSummary(metric, thisPoint, dynamicsDiagnostics ?? null)
        : null;
  const isBugArrival = metric === 'bug-arrival';
  const isBugOrigin = metric === 'bug-origin';
  const isMttr = metric === 'mttr';

  return createPortal(
    <div
      className="issue-comment-overlay issue-comment-overlay--fullscreen"
      role="dialog"
      aria-modal="true"
      aria-label={
        chartTitle ? `${chartTitle} — Issues in ${bucketLabel}` : `Issues in ${bucketLabel}`
      }
      onClick={onClose}
    >
      <div
        className="issue-comment-modal issue-comment-modal--fullscreen chart-card"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="issue-comment-modal__header">
          <div
            className={[
              'issue-comment-modal__title',
              chartTitle ? 'issue-comment-modal__title--with-chart-heading' : '',
            ]
              .filter(Boolean)
              .join(' ')}
          >
            {chartTitle ? (
              <div className="bucket-drilldown-modal__chart-title">{chartTitle}</div>
            ) : null}
            <span>
              Issues in <strong>{bucketLabel}</strong>
              {bucketStart && bucketEnd ? ` · ${bucketStart} → ${bucketEnd}` : ''}
            </span>
          </div>
          <button
            type="button"
            className="issue-comment-modal__close"
            onClick={onClose}
            aria-label="Close"
          >
            <X size={16} />
          </button>
        </div>
        <div className="issue-comment-modal__body-scroll">
          {previewSummary && (
            <p
              className={[
                'quality-breakdown__empty',
                isMttr ? 'bucket-drilldown-mttr-avg-preview' : '',
              ]
                .filter(Boolean)
                .join(' ')}
              style={{ margin: 0 }}
            >
              {previewSummary}
            </p>
          )}
          {isBugOrigin ? (
            <BugOriginDrilldownLegendBar
              series={bugOriginSeriesResolved}
              rosterDriven={Boolean(dynamicsDiagnostics?.bugOriginSeries?.length)}
            />
          ) : null}
          {isMttr ? <MttrDrilldownLegendBar /> : null}
          {error && <p className="quality-breakdown__empty">Failed to load: {error}</p>}
          {!error && rows.length === 0 && !loading && (
            <p className="quality-breakdown__empty">No issues found for this bucket.</p>
          )}
          {rows.length > 0 && (
            <table
              className={[
                'estimation-table',
                isBugArrival ? 'bucket-drilldown-bug-arrival-table' : '',
                isBugOrigin ? 'bucket-drilldown-bug-origin-table' : '',
                isMttr ? 'bucket-drilldown-mttr-table' : '',
              ]
                .filter(Boolean)
                .join(' ')}
            >
              <thead>
                <tr>
                  <th>Key</th>
                  <th>Summary</th>
                  <th>Status</th>
                  <th>Priority</th>
                  <th>{isBugOrigin ? 'Reporter' : 'Assignee'}</th>
                  <th
                    className={
                      isBugArrival ? 'bucket-drilldown-bug-arrival-th--created' : undefined
                    }
                  >
                    Created
                  </th>
                  <th
                    className={
                      isBugArrival ? 'bucket-drilldown-bug-arrival-th--resolved' : undefined
                    }
                  >
                    Resolved
                  </th>
                  {isMttr ? (
                    <th title="Time from created to resolution_date for this issue">
                      Resolve time {parenSuffix}
                    </th>
                  ) : null}
                </tr>
              </thead>
              <tbody>
                {rows.map((r) => {
                  const bs = bucket?.start;
                  const be = bucket?.end;
                  const inCreated =
                    isBugArrival && bs && be && timestampInBucketInclusive(r.created, bs, be);
                  const inResolved =
                    isBugArrival &&
                    bs &&
                    be &&
                    timestampInBucketInclusive(r.resolutionDate, bs, be);
                  const leadH = resolveLeadTimeHours(r.created, r.resolutionDate);
                  return (
                    <tr key={r.key}>
                      <td>
                        <a
                          href={`${JIRA_BROWSE_PREFIX}${encodeURIComponent(r.key)}`}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="task-link"
                        >
                          {r.key}
                        </a>
                      </td>
                      <td>{r.summary}</td>
                      <td>{r.status || '—'}</td>
                      <td className={isMttr ? mttrPriorityCellClass(r.priority) : undefined}>
                        {r.priority || '—'}
                      </td>
                      <td
                        className={
                          isBugOrigin
                            ? bugOriginReporterCellClass(r.reporter, r.reporterRole ?? null)
                            : undefined
                        }
                        style={
                          isBugOrigin
                            ? {
                                color:
                                  bugOriginReporterColor(
                                    r.reporterRole ?? null,
                                    bugOriginSeriesResolved
                                  ) ?? undefined,
                              }
                            : undefined
                        }
                      >
                        {isBugOrigin ? r.reporter || '—' : r.assignee || '—'}
                      </td>
                      <td
                        className={
                          inCreated ? 'bucket-drilldown-td--bug-arrival-created' : undefined
                        }
                      >
                        {r.created ? r.created.slice(0, 10) : '—'}
                      </td>
                      <td
                        className={
                          inResolved ? 'bucket-drilldown-td--bug-arrival-resolved' : undefined
                        }
                      >
                        {r.resolutionDate ? r.resolutionDate.slice(0, 10) : '—'}
                      </td>
                      {isMttr ? (
                        <td className="bucket-drilldown-mttr-resolve-time">
                          {formatLeadTimeDisplay(leadH, unit, toUnitValue, suffix)}
                        </td>
                      ) : null}
                    </tr>
                  );
                })}
              </tbody>
            </table>
          )}
        </div>
        <div className="issue-comment-modal__footer">
          <span className="issue-comment-modal__hint">
            {rows.length} of {total} loaded
          </span>
          <div className="issue-comment-modal__actions">
            {hasMore && (
              <button
                type="button"
                className="btn btn-secondary"
                onClick={() => setOffset(rows.length)}
                disabled={loading}
              >
                {loading ? 'Loading…' : 'Show more'}
              </button>
            )}
            <button type="button" className="btn btn-primary" onClick={onClose}>
              Close
            </button>
          </div>
        </div>
      </div>
    </div>,
    document.body
  );
};

function BugOriginDrilldownLegendBar({
  series,
  rosterDriven,
}: {
  series: ReturnType<typeof resolveBugOriginSeries>;
  rosterDriven: boolean;
}) {
  return (
    <div className="bucket-drilldown-bug-origin-legend-wrap">
      <div className="dynamics-bug-origin-legend-custom">
        {series.map((s) => (
          <span key={s.key} className="dynamics-bug-origin-legend-entry">
            <span
              className="dynamics-bug-origin-legend-swatch"
              style={{ backgroundColor: s.color }}
              aria-hidden
            />
            <span>{s.label}</span>
          </span>
        ))}
      </div>
      <p
        className="bucket-drilldown-bug-origin-legend-note"
        style={{
          margin: '0.4rem 0 0',
          fontSize: '0.75rem',
          lineHeight: 1.45,
          color: 'var(--color-text-secondary)',
        }}
      >
        {rosterDriven
          ? 'Reporter colours follow Team roster roles (matched by name or Jira username). Anyone not on the roster uses the legacy name-pattern fallback.'
          : 'No team roster for this product — QA / Dev / Support come from reporter name patterns only.'}
      </p>
    </div>
  );
}

/** Same stroke colors / labels as `DynamicsD_MTTRDynamics` chart + legend. */
const MTTR_DRILLDOWN_LEGEND: ReadonlyArray<{ label: string; color: string }> = [
  { label: 'Avg MTTR', color: '#0ea5e9' },
  { label: 'Highest (P1)', color: '#ef4444' },
  { label: 'High (P2)', color: '#f97316' },
  { label: 'Medium (P3)', color: '#eab308' },
  { label: 'Low (P4)', color: '#22c55e' },
];

function MttrDrilldownLegendBar() {
  const [avg, ...prios] = MTTR_DRILLDOWN_LEGEND;
  return (
    <div className="bucket-drilldown-mttr-legend-wrap dynamics-mttr-legend-custom">
      <span className="dynamics-mttr-legend-entry">
        <span className="dynamics-mttr-legend-swatch-line" style={{ backgroundColor: avg.color }} />
        <span>{avg.label}</span>
      </span>
      <span className="dynamics-mttr-legend-prio-label">Bug priorities:</span>
      {prios.map((entry) => (
        <span key={entry.label} className="dynamics-mttr-legend-entry">
          <span
            className="dynamics-mttr-legend-swatch-line"
            style={{ backgroundColor: entry.color }}
          />
          <span>{entry.label}</span>
        </span>
      ))}
    </div>
  );
}

function formatMttrBucketPreview(
  point: Record<string, number | string | null>,
  unit: EstimationUnit,
  toUnitValue: (hours: number) => number,
  suffix: string
): string | null {
  const v = point.avg;
  if (typeof v !== 'number') return null;
  const n = toUnitValue(v);
  const s = unit === 'days' ? n.toFixed(1) : String(Math.round(n));
  return `Avg MTTR ${s} ${suffix}`;
}

function resolveLeadTimeHours(
  created: string | null,
  resolutionDate: string | null
): number | null {
  if (!created || !resolutionDate) return null;
  const c = Date.parse(created);
  const r = Date.parse(resolutionDate);
  if (Number.isNaN(c) || Number.isNaN(r) || r < c) return null;
  return (r - c) / 3600000;
}

function formatLeadTimeDisplay(
  hours: number | null,
  unit: EstimationUnit,
  toUnitValue: (h: number) => number,
  suffix: string
): string {
  if (hours == null) return '—';
  const n = toUnitValue(hours);
  const s = unit === 'days' ? n.toFixed(1) : String(Math.round(n));
  return `${s} ${suffix}`;
}

function mttrPriorityCellClass(priority: string | null): string | undefined {
  const s = (priority ?? '').trim().toLowerCase();
  if (!s) return 'bucket-drilldown-prio--none';
  if (s === 'highest' || s === 'p1' || s.includes('highest')) return 'bucket-drilldown-prio--p1';
  if (s === 'high' || s === 'p2' || (s.startsWith('high') && !s.includes('highest')))
    return 'bucket-drilldown-prio--p2';
  if (s === 'medium' || s === 'p3' || s === 'med') return 'bucket-drilldown-prio--p3';
  if (s === 'low' || s === 'lowest' || s === 'p4') return 'bucket-drilldown-prio--p4';
  return undefined;
}

function timestampInBucketInclusive(
  ts: string | null,
  rangeStart: string,
  rangeEnd: string
): boolean {
  if (!ts) return false;
  const t = Date.parse(ts);
  const a = Date.parse(rangeStart);
  const b = Date.parse(rangeEnd);
  if (Number.isNaN(t) || Number.isNaN(a) || Number.isNaN(b)) return false;
  return t >= a && t <= b;
}

function buildPreviewSummary(
  metric: string,
  point: Record<string, number | string | null>,
  dynamicsDiagnostics?: DynamicsDiagnostics | null
): string | null {
  const num = (k: string): number | null => {
    const v = point[k];
    return typeof v === 'number' ? v : null;
  };
  switch (metric) {
    case 'plan-done-debt-vf': {
      const parts = [
        `Plan ${num('plan') ?? 0}`,
        `Done ${num('done') ?? 0}`,
        `VF ${num('verificationFailed') ?? 0}`,
      ];
      return parts.join(' · ');
    }
    case 'bug-arrival': {
      const c = num('created') ?? 0;
      const r = num('resolved') ?? 0;
      return `Created ${c} · Resolved ${r} · Net ${c - r}`;
    }
    case 'bug-flow': {
      return `Open ${num('open') ?? 0} · In QA ${num('inQa') ?? 0} · Done ${num('done') ?? 0}`;
    }
    case 'escaped':
      return `Escaped ${num('escaped') ?? 0}`;
    case 'reopens':
      return `Reopens ${num('reopens') ?? 0}`;
    case 'predictability': {
      const p = num('doneOverPlanPct');
      return p != null ? `Done / Plan: ${p}%` : null;
    }
    case 'bug-origin': {
      const series = resolveBugOriginSeries(dynamicsDiagnostics ?? null);
      return series.map((s) => `${s.label} ${num(s.key) ?? 0}`).join(' · ');
    }
    default:
      return null;
  }
}
